import { ROUTE_QUOTATION_MANAGEMENT } from "@/constants/routes.constants";
import { Card, CardContent, CardHeader } from "@/shadcn/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/shadcn/components/ui/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { DashboardCountQuoteData, SimpleQuotation } from "@/types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Cell, Legend, Pie, PieChart } from "recharts";

type DashboardGraphQuotesProps = {
  quotesCounts: DashboardCountQuoteData;
};

const DashboardGraphQuotes: React.FC<DashboardGraphQuotesProps> = ({
  quotesCounts,
}) => {
  const { t } = useTranslation();
  const nav = useNavigate();

  // Prepare data for the pie chart to visually represent quote statuses.
  const pieData: {
    navKey: SimpleQuotation["status"];
    name: string;
    value: number;
    toolTipText: string;
  }[] = [
    {
      navKey: "open",
      name: t("Open"),
      value: quotesCounts.open,
      toolTipText: t(
        "Your quote has been created and sent. We're waiting for your response or action."
      ),
    },
    {
      navKey: "accepted",
      name: t("Accepted"),
      value: quotesCounts.accepted,
      toolTipText: t(
        "You've approved the quote. We'll proceed with the next steps."
      ),
    },
    {
      navKey: "rejected",
      name: t("Rejected"),
      value: quotesCounts.rejected,
      toolTipText: t(
        "You've declined the quote. No further action will be taken."
      ),
    },
    {
      navKey: "expired",
      name: t("Expired"),
      value:
        quotesCounts.total -
        quotesCounts.open -
        quotesCounts.accepted -
        quotesCounts.rejected,
      toolTipText: t(
        "This quote was open for more than 30 days and is now no longer valid."
      ),
    },
  ];

  function navigateWithFilter(status: SimpleQuotation["status"]) {
    nav(ROUTE_QUOTATION_MANAGEMENT, {
      state: {
        filter: {
          status: status,
        },
      },
    });
  }

  return (
    <Card className="rounded-lg">
      <CardHeader className="text-center">
        <div className="text-xl font-bold">{t("Quotes")}</div>
        <div>Total: {quotesCounts.total}</div>
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={{
            value: { label: t("Quotes"), color: "var(--color-quote-open)" },
          }}
          className="min-h-[200px] w-full"
        >
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={"80%"}
              labelLine={false}
              className="cursor-pointer"
            >
              {pieData.map((entry, idx) => (
                <Cell
                  key={`cell-${idx}`}
                  // fill={"var(--color-quote-open)"}
                  fill={
                    entry.navKey === "open"
                      ? "var(--color-quote-open)"
                      : entry.navKey === "accepted"
                        ? "var(--color-quote-accepted)"
                        : entry.navKey === "rejected"
                          ? "var(--color-quote-rejected)"
                          : "var(--color-quote-expired)"
                  }
                  onClick={() => navigateWithFilter(entry.navKey)}
                />
              ))}
            </Pie>
            <Legend
              align="center"
              verticalAlign="bottom"
              iconType="circle"
              content={({ payload }) => (
                <ul
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 24,
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                  }}
                >
                  {payload &&
                    payload.map((entry, idx) => (
                      <li
                        key={entry.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={() => navigateWithFilter(pieData[idx].navKey)}
                      >
                        <TooltipProvider>
                          <Tooltip delayDuration={150}>
                            <TooltipTrigger>
                              <div className="flex items-center justify-center justify-items-center">
                                <div
                                  className=""
                                  style={{
                                    display: "block",
                                    width: 14,
                                    height: 14,
                                    borderRadius: "50%",
                                    backgroundColor: entry.color,
                                    marginRight: "5px",
                                    cursor: "pointer",
                                  }}
                                />
                                <div
                                  style={{
                                    color: entry.color,
                                    cursor: "pointer",
                                  }}
                                >
                                  {entry.value}
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{pieData[idx].toolTipText}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </li>
                    ))}
                </ul>
              )}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
          </PieChart>
        </ChartContainer>
        <div className="mt-4 flex justify-center">
          <button
            className="group border-primary text-primary relative inline-flex cursor-pointer items-center overflow-hidden rounded-md border-2 px-10 py-2 text-sm font-medium hover:bg-transparent hover:text-black"
            onClick={() => nav(ROUTE_QUOTATION_MANAGEMENT)}
          >
            <span className="ease bg-primary absolute top-1/2 left-0 block h-0 w-full opacity-100 transition-all duration-400 group-hover:top-0 group-hover:h-full"></span>
            <span className="ease absolute -right-2 flex h-10 w-10 translate-x-full transform items-center justify-start duration-300 group-hover:translate-x-0">
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                ></path>
              </svg>
            </span>
            <span className="relative">{t("View All Quotes")}</span>
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardGraphQuotes;
